import { get } from "lodash"
import { numberFormatter } from "helpers/formatters/numbers"
import { generateQueryKey } from "connected-components/dashboard/helpers/query-key"
import { isIntradaySimulationEligible } from "../helpers"

function getExposureSubquery(dateField) {
  return `exposure(contentSetId: $contentSetId) {
        ${dateField}
        factors {
          id
          name
          category
          net
          currencyExposure
        }
      }`
}

function buildQuery(shouldUseIntradayQuery) {
  if (shouldUseIntradayQuery) {
    // IntradayExposure exposes `timestamp: DateTime!`, not `date` — alias it so downstream select stays uniform
    const exposureSubquery = getExposureSubquery("date: timestamp")
    return `#multi-date-exposure
query MultiDateExposureQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!, $contentSetId: String) {
  model(id: $modelId) {
    simulation:intradaySimulation(positionSet: $positionSet, base: $base, on: $on) {
      ${exposureSubquery}
    }
  }
}`
  }

  const exposureSubquery = getExposureSubquery("date")
  return `#multi-date-exposure
query MultiDateExposureQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!, $contentSetId: String) {
  model(id: $modelId) {
    simulation(positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
      ${exposureSubquery}
    }
  }
}`
}

const DAY_BASED_QUERY = buildQuery(false)
const INTRADAY_QUERY = buildQuery(true)

export function createExposureMultiDateQuery(modelId, positionSet, base, from, to, isIntradayMode, options = {}) {
  const shouldUseIntradaySimulation = isIntradaySimulationEligible(isIntradayMode, from, to, options.batchId, options.lastIntradayBatchId)

  const key = generateQueryKey("exposure.multiDate", {
    modelId, positionSet, base, from, to, intraday: shouldUseIntradaySimulation || undefined,
  })

  const sharedVariables = { modelId, positionSet, base }

  const variables = shouldUseIntradaySimulation
    ? { ...sharedVariables, on: from }
    : { ...sharedVariables, from, to }

  return {
    key,
    query: shouldUseIntradaySimulation ? INTRADAY_QUERY : DAY_BASED_QUERY,
    variables,
    select: (graphqlResult) => {
      const exposure = get(graphqlResult, "data.model.simulation.exposure", [])

      return {
        formatter: numberFormatter,
        values: {
          dates: exposure.map(({ date, ...dateData }) => {
            const factors = dateData.factors.reduce((acc, { id, net }) => {
              acc[id] = net
              return acc
            }, {})

            return {
              date,
              ...factors,
            }
          }),
        },
      }
    },
  }
}
